import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { Input } from 'reactstrap';
import { WISH_NOTE_MAX_LENGTH, type Music24WishTopic } from 'fym-shared/src/analytics/wishTopics';
import { TypedMemo } from '../../common/types';
import { withBrandWord } from '../components/primitives/BrandWord';
import { SMLButton } from '../components/primitives/SMLButton';
import { wishBoxMessages as messages } from './wishBoxMessages';
import styles from './WishBox.module.scss';

interface WishBoxFormProps {
  topic: Music24WishTopic | null;
  note: string;
  isSending: boolean;
  failed: boolean;
  onTopic: (topic: Music24WishTopic) => void;
  onNote: (note: string) => void;
  onSend: () => void;
  onClose: () => void;
}

/** Send stays off until the person picks one topic (design wish-box a1). */
const WishBoxFormComponent: React.FC<WishBoxFormProps> = (props) => {
  const { topic, note, isSending, failed, onTopic, onNote, onSend, onClose } = props;
  const intl = useIntl();
  const topics = useMemo<ReadonlyArray<{ id: Music24WishTopic; label: string }>>(
    () => [
      { id: 'studio', label: intl.formatMessage(messages.topicStudio) },
      { id: 'promote', label: intl.formatMessage(messages.topicPromote) },
      { id: 'smartlinks', label: intl.formatMessage(messages.topicSmartlinks) },
      { id: 'outreach', label: intl.formatMessage(messages.topicOutreach) },
      { id: 'analytics', label: intl.formatMessage(messages.topicAnalytics) },
      { id: 'other', label: intl.formatMessage(messages.topicOther) },
    ],
    [intl]
  );
  return (
    <div className={styles.body}>
      <h2 id="wish-box-title" className={styles.title}>
        {withBrandWord(intl.formatMessage(messages.title))}
      </h2>
      {failed ? (
        <div className={styles.alert} role="alert">
          <b>{intl.formatMessage(messages.failedTitle)}</b>
          <span>{intl.formatMessage(messages.failedBody)}</span>
        </div>
      ) : (
        <p className={styles.text}>{intl.formatMessage(messages.body)}</p>
      )}
      <div className={styles.chips} role="group" aria-label={intl.formatMessage(messages.topicsLabel)}>
        {topics.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className={clsx(styles.chip, id === topic && styles.chipOn)}
            aria-pressed={id === topic}
            onClick={() => onTopic(id)}
          >
            {label}
          </button>
        ))}
      </div>
      <div>
        <Input
          type="textarea"
          rows={3}
          value={note}
          maxLength={WISH_NOTE_MAX_LENGTH}
          aria-label={intl.formatMessage(messages.noteLabel)}
          placeholder={intl.formatMessage(messages.notePlaceholder)}
          onChange={(event) => onNote(event.target.value)}
        />
        <div className={styles.count}>
          <span>{intl.formatMessage(messages.teamOnly)}</span>
          <span>{intl.formatMessage(messages.count, { count: note.length, max: WISH_NOTE_MAX_LENGTH })}</span>
        </div>
      </div>
      <div className={styles.actions}>
        <SMLButton onClick={onClose}>{intl.formatMessage(messages.notNow)}</SMLButton>
        <SMLButton variant="primary" disabled={topic === null || isSending} onClick={onSend}>
          {intl.formatMessage(messages.send)}
        </SMLButton>
      </div>
    </div>
  );
};

export const WishBoxForm = TypedMemo(WishBoxFormComponent);
